import { useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useLocation } from 'react-router';

const TRACKING_ID = process.env.REACT_APP_GOOGLE_AD_ID || null;
const TAG_M_ID = process.env.REACT_APP_GOOGLE_TAG_ID || null;

const cookieManager = new Cookies();

export const TRACKING_STATUS = {
    NO: 'NO',
    UNKNOWN: 'UNKNOWN',
    YES: 'YES',
};

export const COOKIE_KEY = 'app_gconsent';

class UserAnalyticFactory {
    constructor() {
        this.gaInitialized = false;
        this.evListen = {};
        this.trackingStatus = TRACKING_STATUS.UNKNOWN;
        this.reloadState();
    }

    reloadState() {
        const restoreTS = cookieManager.get(COOKIE_KEY);
        if (!TRACKING_STATUS[restoreTS]) return;
        this.setTrackingStatus(restoreTS);
    }

    async startTracking() {
        if (!TRACKING_ID) return;
        if (this.gaInitialized) return;
        this.gaInitialized = true;
        this.ReactGA = (await import('react-ga4')).default;
        this.ReactGA.initialize([{
            trackingId: TRACKING_ID,
            debug: true,
            gaOptions: { cookieDomain: 'auto' }
        }, {
            trackingId: TAG_M_ID
        }]);
    }

    getTrackingStatus() {
        return this.trackingStatus;
    }

    setTrackingStatus(value) {
        if (this.trackingStatus != value && TRACKING_STATUS[value]) {
            this.trackingStatus = value;
            cookieManager.set(COOKIE_KEY, TRACKING_STATUS[value]);
            this.uaTrigger('change:tracking-status', this.getTrackingStatus());
        }
        return this;
    }

    trackError() {
        const self = this;
        window.onerror = function (message, source, lineno, colno, error) {
            try {
                self.ReactGA.event({
                    category: 'client_js_error',
                    action: 'js_error',
                    label: `JS ERROR: ${message + ', Source: ' + source + ':  ' + lineno}`
                });
            } catch (e) {
                throw e;
            }
        };
    }

    // Google Analytics

    event(opts) {
        try {
            if (!this.ReactGA) return;
            this.ReactGA.event(opts);
        } catch (e) {
            console.error(e);
        }
    }

    pageView(page) {
        try {
            if (!this.ReactGA) return;
            this.ReactGA.send({ hitType: "pageview", page });
        } catch (e) {
            console.error(e);
        }
    }

    setCurrentUser(entityId, userId) {
        try {
            if (!this.ReactGA) return;
            this.ReactGA.gtag('config', TRACKING_ID, {
                user_id: userId
            });
        } catch (e) {
            console.error(e);
        }
    }
    
    // UserAnalytic Event manager
    uaListen(evName, func) {
        if (!this.evListen[evName]) {
            this.evListen[evName] = [];
        }
        this.evListen[evName].push(func);
    }

    uaTrigger(evName, evValue) {
        const funcs = this.evListen[evName];
        if (!Array.isArray(funcs)) return;
        for (const f of funcs) {
            try {
                f(evValue);
            } catch (e) {
                console.error(e);
            }
        }
    }
};

export const UserAnalytic = new UserAnalyticFactory();

export const UserAnalyticWrapper = ({
    Component,
    props
}) => {
    const [trackingStatus, setTrackingStatus] = useState(UserAnalytic.getTrackingStatus());
    useEffect(() => {
        UserAnalytic.uaListen('change:tracking-status', setTrackingStatus);
    }, []);
    return (
        <Component
            {...props}
            userTrackingStatus={trackingStatus}
        />
    );
};

export const withUserAnalytic = (Component) => {
    return (props) => <UserAnalyticWrapper Component={Component} props={props} />;
};

export const PageTracking = () => {
    const [cPageView, setPageView] = useState(null);
    const location = useLocation();
    useEffect(() => {
        const pathname = location.pathname || null;
        if (pathname != cPageView) {
            setPageView(pathname);
        }
    }, [location]);
    useEffect(() => {
        if (cPageView) {
            UserAnalytic.pageView(cPageView);
        }
    }, [cPageView]);
    return null;
}

export default UserAnalytic;